export type Resample = 'smooth' | 'pixel' | 'area';

export interface ImageAdjustments {
  resample: Resample;
  brightness: number;
  contrast: number;
  saturation: number;
  blur: number;
}

export const NEUTRAL_ADJUSTMENTS: ImageAdjustments = {
  resample: 'area',
  brightness: 1,
  contrast: 1,
  saturation: 1,
  blur: 0,
};

const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const v = i / 255;
  SRGB_TO_LINEAR[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function linearToSrgb(value: number): number {
  const clamped = value <= 0 ? 0 : value >= 1 ? 1 : value;
  const encoded = clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
  return Math.round(encoded * 255);
}

/** How many halving steps to take before the final draw, so bilinear sees every source pixel. */
export function halvingSteps(source: number, target: number): number {
  let steps = 0;
  let size = source;
  while (size / 2 >= target && steps < 8) {
    size /= 2;
    steps += 1;
  }
  return steps;
}

export function prepCacheKey(
  sourceId: string,
  width: number,
  height: number,
  adjustments: ImageAdjustments,
): string {
  const { resample, brightness, contrast, saturation, blur } = adjustments;
  return `${sourceId}|${width}x${height}|${resample}|${brightness}|${contrast}|${saturation}|${blur}`;
}

export function adjustmentFilter(adjustments: ImageAdjustments): string {
  const parts: string[] = [];
  if (adjustments.brightness !== 1) parts.push(`brightness(${adjustments.brightness})`);
  if (adjustments.contrast !== 1) parts.push(`contrast(${adjustments.contrast})`);
  if (adjustments.saturation !== 1) parts.push(`saturate(${adjustments.saturation})`);
  if (adjustments.blur > 0) parts.push(`blur(${adjustments.blur}px)`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

/**
 * Box average in linear light. Averaging sRGB values directly darkens the result, which is what
 * makes a naive downscale of a bright gradient look muddy.
 */
export function boxDownscaleLinear(
  source: Uint8ClampedArray,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(targetWidth * targetHeight * 4);
  const scaleX = sourceWidth / targetWidth;
  const scaleY = sourceHeight / targetHeight;

  for (let y = 0; y < targetHeight; y++) {
    const y0 = Math.floor(y * scaleY);
    const y1 = Math.max(y0 + 1, Math.min(sourceHeight, Math.ceil((y + 1) * scaleY)));
    for (let x = 0; x < targetWidth; x++) {
      const x0 = Math.floor(x * scaleX);
      const x1 = Math.max(x0 + 1, Math.min(sourceWidth, Math.ceil((x + 1) * scaleX)));

      let r = 0;
      let g = 0;
      let b = 0;
      let alpha = 0;
      let weight = 0;
      for (let sy = y0; sy < y1; sy++) {
        for (let sx = x0; sx < x1; sx++) {
          const p = (sy * sourceWidth + sx) * 4;
          const a = source[p + 3] / 255;
          r += SRGB_TO_LINEAR[source[p]] * a;
          g += SRGB_TO_LINEAR[source[p + 1]] * a;
          b += SRGB_TO_LINEAR[source[p + 2]] * a;
          alpha += a;
          weight += 1;
        }
      }

      const target = (y * targetWidth + x) * 4;
      if (alpha <= 0) {
        out[target + 3] = 0;
        continue;
      }
      out[target] = linearToSrgb(r / alpha);
      out[target + 1] = linearToSrgb(g / alpha);
      out[target + 2] = linearToSrgb(b / alpha);
      out[target + 3] = Math.round((alpha / weight) * 255);
    }
  }
  return out;
}

/** Scales one layer's bitmap to its on-canvas size and applies its adjustments, ready to draw. */
export function prepareImage(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  adjustments: ImageAdjustments,
): HTMLCanvasElement {
  const target = document.createElement('canvas');
  target.width = Math.max(1, Math.round(width));
  target.height = Math.max(1, Math.round(height));
  const ctx = target.getContext('2d');
  if (!ctx) return target;

  const filter = adjustmentFilter(adjustments);
  const downscaling = target.width < bitmap.width || target.height < bitmap.height;

  if (adjustments.resample === 'area' && downscaling) {
    const source = document.createElement('canvas');
    source.width = bitmap.width;
    source.height = bitmap.height;
    const sourceCtx = source.getContext('2d');
    if (!sourceCtx) return target;
    sourceCtx.drawImage(bitmap, 0, 0);
    const pixels = sourceCtx.getImageData(0, 0, bitmap.width, bitmap.height).data;
    const scaled = boxDownscaleLinear(pixels, bitmap.width, bitmap.height, target.width, target.height);

    if (filter === 'none') {
      ctx.putImageData(new ImageData(scaled, target.width, target.height), 0, 0);
      return target;
    }
    const staging = document.createElement('canvas');
    staging.width = target.width;
    staging.height = target.height;
    staging.getContext('2d')?.putImageData(new ImageData(scaled, target.width, target.height), 0, 0);
    ctx.filter = filter;
    ctx.drawImage(staging, 0, 0);
    return target;
  }

  if (adjustments.resample === 'pixel') {
    ctx.imageSmoothingEnabled = false;
    ctx.filter = filter;
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);
    return target;
  }

  // Halve repeatedly first: a single bilinear step from a much larger source skips source pixels.
  let stepSource: ImageBitmap | HTMLCanvasElement = bitmap;
  let stepWidth = bitmap.width;
  let stepHeight = bitmap.height;
  const steps = Math.min(
    halvingSteps(bitmap.width, target.width),
    halvingSteps(bitmap.height, target.height),
  );
  for (let i = 0; i < steps; i++) {
    const half = document.createElement('canvas');
    half.width = Math.max(1, Math.round(stepWidth / 2));
    half.height = Math.max(1, Math.round(stepHeight / 2));
    const halfCtx = half.getContext('2d');
    if (!halfCtx) break;
    halfCtx.imageSmoothingEnabled = true;
    halfCtx.imageSmoothingQuality = 'high';
    halfCtx.drawImage(stepSource, 0, 0, half.width, half.height);
    stepSource = half;
    stepWidth = half.width;
    stepHeight = half.height;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.filter = filter;
  ctx.drawImage(stepSource, 0, 0, target.width, target.height);
  return target;
}
